import { create } from 'zustand';
import { api, IGetClothesResponse, IClothes, IGetClothesItem, ICreateClothesRequest, IUpdateClothesRequest } from '../api/api';

// Интерфейс для элемента одежды с адаптацией под текущую систему
export interface ClothesItem {
  id: number;
  brand: string;
  model: string;
  size: string;
  price: string;
  article: string;
  condition: string;
  city: string;
  place: string;
  fitting: boolean;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

// Группированные данные для одежды (аналогично кроссовкам)
export interface GroupedClothesItem {
  article: string;
  brand: string;
  model: string;
  imageUrl: string;
  items: ClothesItem[];
  priceRange: {
    min: number;
    max: number;
  };
}

// Интерфейс состояния стора одежды
interface ClothesState {
  // Данные
  items: ClothesItem[];
  groupedItems: GroupedClothesItem[];

  // Пагинация
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;

  // Состояния загрузки
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;

  // Ошибки
  error: string | null;

  // Флаг инициализации
  hasInitialized: boolean;

  // Поиск
  searchQuery: string;
  isSearching: boolean;
  searchResults: GroupedClothesItem[];
  searchCurrentPage: number;
  searchTotalItems: number;
  searchItemsPerPage: number;

  // Методы для работы с API
  fetchClothes: (page?: number, limit?: number) => Promise<void>;
  fetchClothesById: (id: number) => Promise<ClothesItem>;
  createClothes: (data: Omit<ICreateClothesRequest, 'id'>) => Promise<void>;
  updateClothes: (id: number, data: Partial<ICreateClothesRequest>) => Promise<void>;
  deleteClothes: (id: number) => Promise<void>;

  // Поиск
  searchClothes: (query: string) => void;
  clearSearch: () => void;
  clearSearchResults: () => void;

  // Пагинация
  setPage: (page: number) => void;
  setItemsPerPage: (count: number) => void;
  setSearchPage: (page: number) => void;
  setSearchItemsPerPage: (count: number) => void;
  
  // Сброс состояния
  resetClothesState: () => void;
}

const transformApiDataToClothesItems = (apiResponse: IGetClothesResponse): ClothesItem[] => {
  return apiResponse.data.flatMap(brandGroup =>
    brandGroup.products.flatMap(product =>
      product.clothes.map(item => ({
        id: item.clothes.id,
        brand: typeof item.clothes.brand === 'string' 
          ? item.clothes.brand 
          : (item.clothes.brand as any)?.brand || 'Без бренда',
        model: item.clothes.model || 'Без модели',
        size: typeof item.clothes.size === 'string' 
          ? item.clothes.size 
          : (item.clothes.size as any)?.size || 'Без размера',
        price: `${item.clothes.price} ₽`,
        article: item.clothes.article,
        condition: typeof item.clothes.condition === 'string' 
          ? item.clothes.condition 
          : (item.clothes.condition as any)?.condition || 'Без состояния',
        city: item.clothes.city,
        place: item.clothes.place,
        fitting: item.clothes.fitting,
        imageUrl: item.photo_url || 'https://placehold.co/80x80',
        createdAt: item.clothes.created_at || new Date().toISOString(),
        updatedAt: item.clothes.updated_at || new Date().toISOString()
      }))
    )
  );
};

// Функция для группировки одежды по артикулу
const groupClothesByArticle = (items: ClothesItem[]): GroupedClothesItem[] => {
  const grouped = items.reduce((acc, item) => {
    if (!acc[item.article]) {
      acc[item.article] = {
        article: item.article,
        brand: item.brand,
        model: item.model,
        imageUrl: item.imageUrl || 'https://placehold.co/80x80',
        items: [],
        priceRange: { min: 0, max: 0 }
      };
    }
    acc[item.article].items.push(item);
    return acc;
  }, {} as Record<string, GroupedClothesItem>);

  // Рассчитываем диапазон цен для каждой группы
  return Object.values(grouped).map(group => {
    const prices = group.items.map(item => parseFloat(item.price.replace(/[^\d]/g, '')));
    return {
      ...group,
      priceRange: {
        min: Math.min(...prices),
        max: Math.max(...prices)
      }
    };
  });
};

// Начальное состояние
const initialState = {
  items: [],
  groupedItems: [],
  currentPage: 0,
  totalItems: 0,
  itemsPerPage: 5,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  hasInitialized: false,
  searchQuery: '',
  isSearching: false,
  searchResults: [],
  searchCurrentPage: 0,
  searchTotalItems: 0,
  searchItemsPerPage: 5,
};

// Создание стора одежды
export const useClothesStore = create<ClothesState>()((set, get) => ({
  ...initialState,

  fetchClothes: async (page?: number, limit?: number) => {
    set({ isLoading: true, error: null });
    
    const state = get();

    try {
      const currentPage = page ?? state.currentPage;
      const currentLimit = limit ?? state.itemsPerPage;
      const offset = currentPage * currentLimit;

      const response = await api.getClothes({
        offset,
        limit: currentLimit
      });
      
      const items = transformApiDataToClothesItems(response);
      const groupedItems = groupClothesByArticle(items);

      set({
        items,
        groupedItems,
        totalItems: response.total_inventory_count,
        currentPage,
        itemsPerPage: currentLimit,
        isLoading: false,
        hasInitialized: true
      });

      return Promise.resolve();
    } catch (error) {
      console.error('Ошибка при загрузке одежды:', error);
      set({
        error: error instanceof Error ? error.message : 'Неизвестная ошибка при загрузке одежды',
        isLoading: false,
        hasInitialized: true
      });
      return Promise.reject(error);
    }
  },

  // Получение конкретного элемента одежды по ID
  fetchClothesById: async (id: number): Promise<ClothesItem> => {
    set({ isLoading: true, error: null });

    try {
      const response = await api.getClothesById(id);
      const item = {
        id: response.clothes.id,
        brand: typeof response.clothes.brand === 'string' 
          ? response.clothes.brand 
          : (response.clothes.brand as any)?.brand || 'Без бренда',
        model: response.clothes.model || 'Без модели',
        size: typeof response.clothes.size === 'string' 
          ? response.clothes.size 
          : (response.clothes.size as any)?.size || 'Без размера',
        price: `${response.clothes.price} ₽`,
        article: response.clothes.article,
        condition: typeof response.clothes.condition === 'string' 
          ? response.clothes.condition 
          : (response.clothes.condition as any)?.condition || 'Без состояния',
        city: response.clothes.city,
        place: response.clothes.place,
        fitting: response.clothes.fitting,
        imageUrl: response.photo_url || 'https://placehold.co/80x80',
        createdAt: response.clothes.created_at || new Date().toISOString(),
        updatedAt: response.clothes.updated_at || new Date().toISOString()
      };

      set({ isLoading: false });
      return Promise.resolve(item);
    } catch (error) {
      console.error('Ошибка при загрузке элемента одежды:', error);
      set({
        error: error instanceof Error ? error.message : 'Ошибка при загрузке элемента одежды',
        isLoading: false
      });
      return Promise.reject(error);
    }
  },

  // Создание нового элемента одежды
  createClothes: async (data: Omit<ICreateClothesRequest, 'id'>) => {
    set({ isCreating: true, error: null });

    try {
      await api.createClothes(data);
      await get().fetchClothes();

      set({ isCreating: false });
      return Promise.resolve();
    } catch (error) {
      console.error('Ошибка при создании одежды:', error);
      set({
        error: error instanceof Error ? error.message : 'Ошибка при создании одежды',
        isCreating: false
      });
      return Promise.reject(error);
    }
  },

  // Обновление элемента одежды
  updateClothes: async (id: number, data: Partial<ICreateClothesRequest>) => {
    set({ isUpdating: true, error: null });

    try {
      const updateData: IUpdateClothesRequest = {
        id,
        size: data.size || '',
        brand: data.brand || '',
        price: data.price || 0,
        article: data.article || '',
        condition: data.condition || '',
        city: data.city || '',
        place: data.place || '',
        fitting: data.fitting || false
      };

      await api.updateClothes(updateData);
      await get().fetchClothes();

      set({ isUpdating: false });
      return Promise.resolve();
    } catch (error) {
      console.error('Ошибка при обновлении одежды:', error);
      set({
        error: error instanceof Error ? error.message : 'Ошибка при обновлении одежды',
        isUpdating: false
      });
      return Promise.reject(error);
    }
  },

  // Удаление элемента одежды
  deleteClothes: async (id: number) => {
    set({ isDeleting: true, error: null });

    try {
      await api.deleteClothes(id);
      await get().fetchClothes();

      set({ isDeleting: false });
      return Promise.resolve();
    } catch (error) {
      console.error('Ошибка при удалении одежды:', error);
      set({
        error: error instanceof Error ? error.message : 'Ошибка при удалении одежды',
        isDeleting: false
      });
      return Promise.reject(error);
    }
  },

  // Поиск одежды
  searchClothes: (query: string) => {
    if (!query.trim()) {
      set({ 
        searchResults: [], 
        searchQuery: '', 
        isSearching: false,
        searchCurrentPage: 0,
        searchTotalItems: 0 
      });
      return;
    }

    set({ isSearching: true, searchQuery: query });

    const { groupedItems } = get();
    const filteredItems = groupedItems.filter((group: GroupedClothesItem) =>
      group.brand.toLowerCase().includes(query.toLowerCase()) ||
      group.model.toLowerCase().includes(query.toLowerCase()) ||
      group.article.toLowerCase().includes(query.toLowerCase()) ||
      group.items.some(item => 
        item.condition.toLowerCase().includes(query.toLowerCase()) ||
        item.city.toLowerCase().includes(query.toLowerCase()) ||
        item.place.toLowerCase().includes(query.toLowerCase())
      )
    );

    set({ 
      searchResults: filteredItems,
      searchTotalItems: filteredItems.length,
      isSearching: false
    });
  },

  // Очистка поиска
  clearSearch: () => {
    set({ 
      searchQuery: '', 
      searchResults: [], 
      isSearching: false,
      searchCurrentPage: 0,
      searchTotalItems: 0 
    });
  },

  // Очистка только результатов поиска
  clearSearchResults: () => {
    set({ 
      searchResults: [], 
      isSearching: false,
      searchCurrentPage: 0,
      searchTotalItems: 0 
    });
  },

  // Установка текущей страницы
  setPage: (page: number) => {
    const state = get();
    set({ currentPage: page, isLoading: true });
    // Загружаем данные для новой страницы
    state.fetchClothes(page, state.itemsPerPage);
  },

  // Установка количества элементов на странице
  setItemsPerPage: (count: number) => {
    const state = get();
    set({ itemsPerPage: count, currentPage: 0, isLoading: true });
    // Загружаем данные с новым размером страницы
    state.fetchClothes(0, count);
  },

  // Установка текущей страницы поиска
  setSearchPage: (page: number) => {
    set({ searchCurrentPage: page });
  },

  // Установка количества элементов на странице поиска
  setSearchItemsPerPage: (count: number) => {
    set({ searchItemsPerPage: count, searchCurrentPage: 0 });
  },
  
  // Сброс состояния одежды
  resetClothesState: () => {
    set({
      ...initialState
    });
  },
}));
